import { Font, Text, Link, View, StyleSheet } from '@react-pdf/renderer';
import { ensureUrlHasScheme } from '../../utils';

const NodeType = {
  ELEMENT: 1,
  TEXT: 3,
};

// Helper to create a DOM-like structure that works in both browser and Node.js
const createDOMElement = (
  html: string,
): { childNodes: NodeListOf<ChildNode> | Array<ChildNode> } => {
  // Browser environment
  if (typeof document !== 'undefined' && 'createElement' in document) {
    const div = document.createElement('div');
    div.innerHTML = html;
    return div;
  }

  // Node.js environment - use jsdom
  // eslint-disable-next-line @typescript-eslint/no-require-imports, @typescript-eslint/no-unsafe-assignment
  const { JSDOM } = require('jsdom');
  // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-assignment
  const dom = new JSDOM(`<div>${html}</div>`);
  // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
  const divElement = dom.window.document.querySelector('div');
  // eslint-disable-next-line @typescript-eslint/no-unsafe-return
  return divElement ?? { childNodes: [] };
};

const commonStyles = StyleSheet.create({
  column: {},
  text: {
    fontFamily: 'MaisonNeue',
  },
  bold: {
    fontFamily: 'MaisonNeue-Bold',
  },
  italic: {
    fontFamily: 'MaisonNeue-BookItalic',
    fontStyle: 'italic',
  },
  underline: {
    textDecoration: 'underline',
  },
  listItem: {
    display: 'flex',
    flexDirection: 'row',
  },
  list: {
    marginTop: 3,
    marginBottom: 3,
  },
});

const elementStyles = {
  B: commonStyles.bold,
  STRONG: commonStyles.bold,
  I: commonStyles.italic,
  U: commonStyles.underline,
  LI: commonStyles.listItem,
  UL: commonStyles.list,
  OL: commonStyles.list,
};

Font.registerHyphenationCallback((word) => [word]);

const isElement = (node: ChildNode) => node.nodeType === NodeType.ELEMENT;
const isParagraph = (node: ChildNode) => node.nodeName === 'P';
const FURTHER_BLANK_ATTR_SNIPPET = 'data-further-blank="1"';

const removeEmptyPTags = (content: string) =>
  content.replace(/<p[^>]*>\s*<\/p>/gi, (match) =>
    match.includes(FURTHER_BLANK_ATTR_SNIPPET) ? match : '',
  );

const replaceBrWithPTags = (content: string) =>
  content.replace(/(<br\s*\/?><br\s*\/?>)/g, '</p><p>');

export const htmlToPDF = ({
  content,
  wrapParagraphs,
  primaryColor,
  underlineLinks,
}: {
  content: string;
  wrapParagraphs?: boolean;
  primaryColor?: string;
  underlineLinks?: boolean;
}) => {
  const div = createDOMElement(replaceBrWithPTags(removeEmptyPTags(content)));

  const transformNodes = (
    nodes: NodeListOf<ChildNode> | Array<ChildNode>,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
  ): Array<any> =>
    Array.from(nodes).map((element, index) => {
      if ('href' in element) {
        const href = element.href as string;
        const src = ensureUrlHasScheme(href);
        return (
          <Link
            src={src}
            wrap={wrapParagraphs}
            key={element.textContent}
            style={{
              color: primaryColor ?? '#000000',
              textDecoration: underlineLinks ? 'underline' : 'none',
            }}
            // target is not supported in react-pdf to open in new tab
          >
            {element.textContent}
          </Link>
        );
      }
      if (element.nodeType === NodeType.TEXT) {
        return element.textContent?.length ? (
          <Text
            key={element.textContent}
            style={commonStyles.text}
            wrap={wrapParagraphs}
          >
            {element.textContent}
          </Text>
        ) : null;
      }

      if (isElement(element)) {
        if (isParagraph(element)) {
          const paragraphText = element.textContent ?? '';
          if (!paragraphText.trim()) {
            const isBlankMarker =
              // @ts-expect-error input unknown
              element.getAttribute?.('data-further-blank') === '1'; // eslint-disable-line @typescript-eslint/no-unsafe-call
            if (!isBlankMarker) return null;

            return (
              // eslint-disable-next-line react/no-array-index-key
              <View key={`empty-line-${index}`} style={{ height: 12 }} />
            );
          }

          if (element.childNodes.length) {
            const children = transformNodes(element.childNodes).filter(
              (child) => !!child,
            );
            return children.length ? (
              <Text
                key={element.textContent}
                wrap={wrapParagraphs}
                style={{
                  margin: 0,
                  padding: 0,
                }}
              >
                <>{children ?? null}</>
              </Text>
            ) : null;
          }
          return null;
        }

        if (element.nodeName === 'UL' || element.nodeName === 'OL') {
          const children = transformNodes(element.childNodes).filter(
            (child) => !!child,
          );
          const isNested = element.parentNode?.nodeName === 'LI';

          return children.length ? (
            <View
              key={element.textContent}
              style={[
                elementStyles[element.nodeName],
                isNested ? { marginBottom: 0, marginTop: 4 } : {},
              ]}
            >
              {children}
            </View>
          ) : null;
        }

        if (element.nodeName === 'LI') {
          const isOrdered = element.parentNode?.nodeName === 'OL';
          const index = Array.from(element.parentNode?.children ?? []).indexOf(
            // @ts-expect-error input unknown
            element,
          );
          const bullet = isOrdered ? `${index + 1}.` : '•';
          const isLastItem =
            index === (element.parentNode?.children?.length ?? 0) - 1;
          const children = transformNodes(element.childNodes).filter(
            (child) => !!child,
          );

          return (
            <View
              key={element.textContent}
              style={[elementStyles.LI, isLastItem ? { marginBottom: 0 } : {}]}
            >
              <Text style={{ width: 8, marginRight: 1 }}>{bullet}</Text>
              <Text style={{ flex: 1 }} wrap={wrapParagraphs}>
                {children}
              </Text>
            </View>
          );
        }

        return element.textContent?.length ? (
          <Text
            key={element.textContent}
            style={
              elementStyles[element.nodeName as keyof typeof elementStyles] ??
              commonStyles.text
            }
            wrap={wrapParagraphs}
          >
            {element.textContent}
          </Text>
        ) : null;
      }
      return null;
    });

  if (div.childNodes.length === 1 && !isElement(div.childNodes[0])) {
    return (
      <View style={commonStyles.column}>
        <Text style={{ margin: 0, padding: 0 }}>
          {transformNodes(div.childNodes)}
        </Text>
      </View>
    );
  }
  return (
    <View style={commonStyles.column}>{transformNodes(div.childNodes)}</View>
  );
};
